import React from "react";
import { Box, Alert, CircularProgress } from "@mui/joy";
import { useLeave } from "../../contexts/api";
import { useAlert } from "../../contexts/app/notificationToast";
import { useAuth } from "../../contexts/api";
import { useHRData } from "../../contexts/app/hrData";
import { usePermissions, Permission } from "../../contexts/app/permissions";
import type { UserWithRole } from "../../api/auth/Auth.typed";
import LeaveBalanceTypeSelector from "./display/LeaveBalanceTypeSelector";
import LeaveBalanceAssociateBar from "./display/LeaveBalanceAssociateBar";
import LeaveBalanceTable from "./display/LeaveBalanceTable";
import type { UserWithBalance } from "./display/LeaveBalanceTable";
import DarkCard from "../common/DarkCard";
import DarkTypography from "../common/DarkTypography";
import { hrSheetSx } from "../../styles/globalStyles";
import { userDisplayName } from "../../utils/userHelper";
import { useConfirmationModal } from "../../contexts/app/confirmation";
import { NO_ACCESS_MESSAGE } from "../common/DashboardPage";

export default function LeaveBalanceComponent() {
  const { listLeaveTypeBalances, setLeaveTypeBalance, deleteLeaveTypeBalance } = useLeave();
  const { showAlert } = useAlert();
  const { getUsers } = useAuth();
  const { leaveTypes } = useHRData();
  const { hasPermission } = usePermissions();
  const canAccessBalances =
    hasPermission(Permission.LEAVE_ACCESS) && hasPermission(Permission.LEAVE_MANAGE_BALANCES);
  const modal = useConfirmationModal();

  const [usersWithBalances, setUsersWithBalances] = React.useState<UserWithBalance[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [editingUserId, setEditingUserId] = React.useState<number | null>(null);
  const [editValue, setEditValue] = React.useState("");
  const [updateLoading, setUpdateLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [selectedTypeId, setSelectedTypeId] = React.useState<number>(-1);
  const [employees, setEmployees] = React.useState<UserWithRole[]>([]);
  const [selectedUserIdToAdd, setSelectedUserIdToAdd] = React.useState<number>(-1);

  const loadBalances = React.useCallback(async () => {
    if (!canAccessBalances) {
      setUsersWithBalances([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      if (selectedTypeId === -1) {
        setUsersWithBalances([]);
        setLoading(false);
        return;
      }
      const paged = await listLeaveTypeBalances(selectedTypeId, 1, 100);
      const items = paged?.data || [];
      const combined: UserWithBalance[] = items.map((b) => {
        const emp = employees.find((e) => e.user.id === b.UserID);
        return {
          user: {
            id: b.UserID,
            first_name: emp?.user?.first_name ?? "",
            last_name: emp?.user?.last_name ?? "",
            email: emp?.user?.email ?? "",
          },
          balance: b,
        };
      });
      setUsersWithBalances(combined);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Erreur lors du chargement des données";
      setError(msg);
      showAlert(msg, "danger");
    } finally {
      setLoading(false);
    }
  }, [canAccessBalances, listLeaveTypeBalances, selectedTypeId, employees, showAlert]);

  React.useEffect(() => {
    if (!canAccessBalances) {
      setEmployees([]);
      return;
    }
    getUsers(true).then((list) => {
      if (Array.isArray(list)) setEmployees(list);
    });
  }, [canAccessBalances, getUsers]);

  React.useEffect(() => {
    loadBalances();
  }, [loadBalances, selectedTypeId]);

  const canEdit = React.useCallback(
    () => hasPermission(Permission.LEAVE_MANAGE_BALANCES),
    [hasPermission],
  );
  const selectedType = React.useMemo(
    () => leaveTypes.find((t) => t.ID === selectedTypeId),
    [leaveTypes, selectedTypeId],
  );
  const countersEnabled = !!selectedType?.UsesCounter;

  const handleStartEdit = React.useCallback(
    (ub: UserWithBalance) => {
      if (!countersEnabled) return;
      setEditingUserId(ub.user.id);
      setEditValue(String(ub.balance?.Balance ?? "0"));
    },
    [countersEnabled],
  );

  // API/DB only use point for decimals; normalize comma to point before parsing
  const parseDecimalBalance = React.useCallback((s: string): number => {
    const normalized = s.trim().replace(",", ".");
    return parseFloat(normalized);
  }, []);

  const handleSaveEdit = React.useCallback(
    async (ub: UserWithBalance) => {
      if (!countersEnabled) return;
      const newValue = parseDecimalBalance(editValue);
      if (Number.isNaN(newValue) || editingUserId !== ub.user.id) return;
      setUpdateLoading(true);
      try {
        if (selectedTypeId === -1) return;
        const ok = await modal.showConfirmation(
          "Mettre à jour le solde ?",
          `Nouveau solde: ${newValue}`,
        );
        if (!ok) {
          setUpdateLoading(false);
          return;
        }
        await setLeaveTypeBalance(selectedTypeId, ub.user.id, { Balance: newValue });
        showAlert(`Solde mis à jour pour ${userDisplayName(ub.user)}`, "success");
        loadBalances();
        setEditingUserId(null);
        setEditValue("");
      } catch {
      } finally {
        setUpdateLoading(false);
      }
    },
    [
      countersEnabled,
      editValue,
      editingUserId,
      loadBalances,
      modal,
      parseDecimalBalance,
      selectedTypeId,
      setLeaveTypeBalance,
      showAlert,
    ],
  );

  const handleCancelEdit = React.useCallback(() => {
    setEditingUserId(null);
    setEditValue("");
  }, []);

  const formatDate = React.useCallback((dateString: string) => {
    return new Date(dateString).toLocaleDateString("fr-FR", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }, []);

  const getBalanceColor = React.useCallback((days: number) => {
    if (days > 10) return "#4CAF50";
    if (days >= 0) return "#FF9800";
    return "#f44336";
  }, []);

  const availableEmployees = React.useMemo(() => {
    const assigned = new Set(usersWithBalances.map((u) => u.user.id));
    return employees.filter((e) => !assigned.has(e.user.id));
  }, [employees, usersWithBalances]);

  const handleAssociateUser = React.useCallback(async () => {
    if (selectedTypeId === -1 || selectedUserIdToAdd === -1) return;
    setUpdateLoading(true);
    try {
      const ok = await modal.showConfirmation("Associer ce salarié au type de congé ?");
      if (!ok) {
        setUpdateLoading(false);
        return;
      }
      await setLeaveTypeBalance(selectedTypeId, selectedUserIdToAdd, { Balance: 0 });
      await loadBalances();
      setSelectedUserIdToAdd(-1);
      showAlert("Salarié associé", "success");
    } catch (err) {
      if (err instanceof Error) showAlert(err.message, "danger");
    } finally {
      setUpdateLoading(false);
    }
  }, [loadBalances, modal, selectedTypeId, selectedUserIdToAdd, setLeaveTypeBalance, showAlert]);

  const handleDissociate = React.useCallback(
    async (ub: UserWithBalance) => {
      if (selectedTypeId === -1) return;
      setUpdateLoading(true);
      try {
        const ok = await modal.showConfirmation(
          "Dissocier ce salarié du type de congé ?",
          `Le solde de ${userDisplayName(ub.user)} pour ce type sera supprimé.`,
        );
        if (!ok) {
          setUpdateLoading(false);
          return;
        }
        await deleteLeaveTypeBalance(selectedTypeId, ub.user.id);
        await loadBalances();
        showAlert("Salarié dissocié", "success");
      } catch (err) {
        if (err instanceof Error) showAlert(err.message, "danger");
      } finally {
        setUpdateLoading(false);
      }
    },
    [deleteLeaveTypeBalance, loadBalances, modal, selectedTypeId, showAlert],
  );

  if (!canAccessBalances) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flex: 1,
          minHeight: "50vh",
          p: 3,
        }}
      >
        <DarkTypography
          level="body-lg"
          colorVariant="muted"
          sx={{ textAlign: "center", maxWidth: 400 }}
        >
          {NO_ACCESS_MESSAGE}
        </DarkTypography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      {error && (
        <Alert color="danger" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <LeaveBalanceTypeSelector
        leaveTypes={leaveTypes}
        selectedTypeId={selectedTypeId}
        onTypeChange={setSelectedTypeId}
      />
      {selectedTypeId === -1 ? (
        <DarkCard sx={{ p: 4, textAlign: "center" }}>
          <DarkTypography level="body-lg">
            Sélectionnez un type de congé pour voir les soldes.
          </DarkTypography>
        </DarkCard>
      ) : (
        <Box sx={hrSheetSx}>
          {canEdit() && (
            <LeaveBalanceAssociateBar
              availableEmployees={availableEmployees}
              selectedUserIdToAdd={selectedUserIdToAdd}
              onUserChange={setSelectedUserIdToAdd}
              onAssociate={handleAssociateUser}
              disabled={updateLoading}
            />
          )}
          <LeaveBalanceTable
            usersWithBalances={usersWithBalances}
            countersEnabled={countersEnabled}
            canEdit={canEdit()}
            editingUserId={editingUserId}
            editValue={editValue}
            updateLoading={updateLoading}
            onEditValueChange={setEditValue}
            onStartEdit={handleStartEdit}
            onSaveEdit={handleSaveEdit}
            onCancelEdit={handleCancelEdit}
            onDissociate={handleDissociate}
            getBalanceColor={getBalanceColor}
            formatDate={formatDate}
          />
        </Box>
      )}
    </Box>
  );
}
